import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    previewOrthopulseOrder,
    sendOrthopulseOrder,
    type ForceOverridePayload,
    type ShipToPayload
} from '../services/orthopulseOrder';
import {
    BILLING_CHARGE_KEY,
    BILLING_CHARGES_KEY,
    BILLING_PURCHASE_ORDERS_KEY,
    ORTHOPULSE_ORDER_PREVIEW_KEY
} from './queryKeys';

interface OrthopulseOrderMutationVars {
    shipTo?: ShipToPayload;
    forceOverride?: ForceOverridePayload;
}

// staleTime: 0 / retry: false mirror useSparkAccountRequestPreview - a stale
// blocked/vendor snapshot is actively misleading here. Called without shipTo:
// this is the mount-time preview that only needs `blocked` + `shipToDefaults`.
export function useOrthopulseOrderPreview(patientId: string, chargeId?: string) {
    return useQuery({
        queryKey: ORTHOPULSE_ORDER_PREVIEW_KEY(patientId, chargeId),
        queryFn: () => previewOrthopulseOrder(patientId, chargeId),
        staleTime: 0,
        retry: false
    });
}

// User-triggered re-preview once a ship-to has been chosen (the "Preview
// email" step). A mutation rather than a query: it must run on demand with
// the current form values, not refetch automatically.
export function usePreviewOrthopulseOrderWithShipTo(patientId: string, chargeId?: string) {
    return useMutation({
        mutationFn: ({ shipTo, forceOverride }: OrthopulseOrderMutationVars) =>
            previewOrthopulseOrder(patientId, chargeId, shipTo, forceOverride)
    });
}

// Invalidates the timeline rail, the preview, the charges list (every send
// either creates or updates a charge), the single charge when targeted, and
// the purchase orders this send just raised - the bare key reaches the paged,
// infinite and detail shapes by prefix.
export function useSendOrthopulseOrder(patientId: string, chargeId?: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (vars?: OrthopulseOrderMutationVars) =>
            sendOrthopulseOrder(patientId, chargeId, vars?.shipTo, vars?.forceOverride),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['patient-timeline', patientId] });
            queryClient.invalidateQueries({ queryKey: ['patient-journey', patientId] });
            queryClient.invalidateQueries({
                queryKey: ORTHOPULSE_ORDER_PREVIEW_KEY(patientId, chargeId)
            });
            queryClient.invalidateQueries({ queryKey: [BILLING_CHARGES_KEY] });
            queryClient.invalidateQueries({ queryKey: [BILLING_PURCHASE_ORDERS_KEY] });
            if (chargeId) {
                queryClient.invalidateQueries({ queryKey: BILLING_CHARGE_KEY(chargeId) });
            }
        }
    });
}
